import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import styled from "@emotion/styled";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import CircularProgress from "@mui/material/CircularProgress";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import RefreshIcon from "@mui/icons-material/Refresh";
import LogoutIcon from '@mui/icons-material/Logout';
import CategoryIcon from "@mui/icons-material/Category";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import MapIcon from "@mui/icons-material/Map";
import FeedOutlinedIcon from "@mui/icons-material/FeedOutlined";
import VideoCallOutlinedIcon from "@mui/icons-material/VideoCallOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import GroupIcon from "@mui/icons-material/Group";
import FollowTheSignsIcon from '@mui/icons-material/FollowTheSigns';
import Colors from "../components/ui/Colors";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import TenantSwitcher from "../components/TenantSwitcher";
import { clearSession } from "../utilityFunctions/session";
import { refreshTenantSession } from "../utilityFunctions/TenantUtil";
import { NAVBAR_HEIGHT, NAVBAR_Z_INDEX } from "./layoutMetrics";
import { useSidebar } from "./SidebarContext";

/**
 * The bar is hard-bounded to NAVBAR_HEIGHT and clips its own children.
 *
 * Previously the logo was sized as a percentage of the viewport width with
 * `height: auto`, so on wide screens it rendered ~225px tall inside a 75px bar.
 * Nothing clipped it, so the navbar's inner boxes grew to the height of the
 * image and — being inside a `position: fixed`, z-indexed element — laid an
 * invisible, full-width, click-swallowing strip over the top ~190px of every
 * page. That is why the "+ new session" button in the chat workspace stopped
 * responding on large monitors (and only there: on narrow screens the
 * overflow was too short to reach it).
 */
const NavBarStyle = styled.div({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  flexWrap: "nowrap",
  height: NAVBAR_HEIGHT,
  minHeight: NAVBAR_HEIGHT,
  maxHeight: NAVBAR_HEIGHT,
  width: "100%",
  backgroundColor: "#242223",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  // Belt and braces: even if a child ever overflows again it can neither be
  // seen nor receive pointer events outside the bar.
  overflow: "hidden",
  zIndex: NAVBAR_Z_INDEX,
});

const NavItem = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  color: "white",
  cursor: "pointer",
  textAlign: "center",
});

const NavBar = ({ loginUser, userdata, showMenuButton = false }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { toggleSidebar } = useSidebar();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [profile, setProfile] = useState(null);

  // "Refresh access": re-fetch the current tenant's effective permissions with
  // the existing JWT (no re-login, no tenant switch) so the permission-gated
  // menu catches up after ROOT edits a role. Throttled for the auto (focus)
  // trigger so returning to the tab doesn't spam the backend.
  const [refreshing, setRefreshing] = useState(false);
  const [refreshSnack, setRefreshSnack] = useState({ open: false, message: "" });
  const lastRefreshRef = useRef(0);
  const REFRESH_THROTTLE_MS = 30000;

  const runAccessRefresh = useCallback(async (manual) => {
    const jwt = loginUser && loginUser.jwtToken;
    if (!jwt) return; // only when logged in
    const now = Date.now();
    if (!manual && now - lastRefreshRef.current < REFRESH_THROTTLE_MS) return;
    lastRefreshRef.current = now;
    if (manual) setRefreshing(true);
    const merged = await refreshTenantSession(jwt, loginUser.memberships);
    if (merged) userdata(merged); // rebuilds the permission-gated menu
    if (manual) {
      setRefreshing(false);
      setRefreshSnack({
        open: true,
        message: merged ? "Access refreshed" : "Access is already up to date",
      });
    }
  }, [loginUser, userdata]);

  // Automatic, throttled refresh when the user returns to the tab.
  useEffect(() => {
    const onFocus = () => { runAccessRefresh(false); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [runAccessRefresh]);

  useEffect(() => {
    const initValues = async () => {
      handleProfile();
    };
    initValues();
  }, []);

  const handleProfile = async () => {
    if (
      loginUser.roles &&
      !(Object.keys(loginUser.roles).length === 0 && loginUser.roles.constructor === Object)
    ) {
      setProfile(loginUser.roles[0].authority);
      console.log(loginUser.roles[0].authority);
    }
  };

  const handleClickMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const logoutHandler = async () => {
    clearSession();
    localStorage.clear();
    userdata(null);
    history.push("/");
  }


  const menuItems = [
    { id: "gracePlan", icon: <FollowTheSignsIcon sx={{ color: Colors.primary }} />, text: t("NavBar.gracePlan"), action: () => {handleClose(); history.push("/consumer/llm_choose")} },
    { id: "planCharts", icon: <SignalCellularAltIcon sx={{ color: Colors.primary }} />, text: t("NavBar.planCharts"), action: () => {handleClose(); history.push("/retirement_plan/dashboard")} },
    { id: "retirementJournal", icon: <FeedOutlinedIcon sx={{ color: "#BDBDBD" }} />, text: t("NavBar.retirementJournal"), action: handleClose },
    { id: "meetingsRecord", icon: <VideoCallOutlinedIcon sx={{ color: "#BDBDBD" }} />, text: t("NavBar.meetingsRecord"), action: handleClose },
    { id: "exploreTopics", icon: <TravelExploreOutlinedIcon sx={{ color: "#BDBDBD" }} />, text: t("NavBar.exploreTopics"), action: handleClose },
    { id: "resourcesInsights", icon: <TipsAndUpdatesIcon sx={{ color: "#BDBDBD" }} />, text: t("NavBar.resourcesInsights"), action: handleClose },
    { id: "addAdvisor", icon: <PersonSearchIcon sx={{ color: Colors.primary }} />, text: t("NavBar.connectWithAdvisor"), action: () => {handleClose(); history.push("/consumer/add_advisor")} },
    { id: "friend", icon: <GroupIcon sx={{ color: "#BDBDBD" }} />, text: t("NavBar.referAFriend"), action: handleClose },
  ];

  const navMenus = [  
    { id: "actions", 
      icon: <Avatar alt="Actions" sx={{ backgroundColor: "white", color: "black" }}><CategoryIcon sx={{ fontSize: "1.5rem" }} /></Avatar>,
    },
    { id: "logOut", 
      // to: "/", 
      icon: <Avatar alt="Home" sx={{ backgroundColor: "white", color: "black" }}><LogoutIcon sx={{ fontSize: "1.5rem" }} /></Avatar>,
      action: () => logoutHandler()
    }
  ];

  const roleNavMenus = [
    { id: "home", 
      // to: "/handle_profile", 
      icon: <Avatar alt="Home" sx={{ backgroundColor: "white", color: "black" }}><HomeIcon sx={{ fontSize: "1.5rem" }} /></Avatar>,
      action: () => history.push("/handle_profile")
    },
    { id: "logOut", 
      // to: "/", 
      icon: <Avatar alt="Home" sx={{ backgroundColor: "white", color: "black" }}><LogoutIcon sx={{ fontSize: "1.5rem" }} /></Avatar>,
      action: () => logoutHandler()
    },
  ]

  return (
    <>
    <NavBarStyle>
      {/* Hamburger — only below md, where the sidebar is a temporary drawer. */}
      {showMenuButton && (
        <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", pl: "0.5rem" }}>
          <IconButton onClick={toggleSidebar} aria-label="open navigation menu" sx={{ color: "white" }}>
            <MenuIcon />
          </IconButton>
        </Box>
      )}

      {/* Logo — height-bounded so it can never outgrow the bar. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          pl: showMenuButton
            ? { xs: "0.25rem", md: "1.5rem" }
            : { xs: "0.9rem", sm: "1rem", md: "1.5rem" },
        }}
      >
        <Box
          component="img"
          sx={{
            height: { xs: "2.2rem", sm: "2.6rem", md: "3rem" },
            width: "auto",
            maxWidth: "100%",
            objectFit: "contain",
            display: "block",
          }}
          alt="Logo"
          src="/images/logo_datalove_white.png"
        />
      </Box>

      {/* Navigation actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          flexShrink: 0,
          gap: { xs: "0.4rem", sm: "0.75rem" },
          pr: { xs: "0.4rem", sm: "1rem" },
        }}
      >
        <TenantSwitcher loginUser={loginUser} userdata={userdata} />
        {loginUser && loginUser.jwtToken && (
          <Tooltip title="Refresh access (pick up newly granted features without re-login)">
            <span>
              <IconButton
                aria-label="refresh access"
                onClick={() => runAccessRefresh(true)}
                disabled={refreshing}
                sx={{ color: "white" }}
              >
                {refreshing
                  ? <CircularProgress size={20} sx={{ color: "white" }} />
                  : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {roleNavMenus.map((menu) => (
          <NavItem
            key={menu.id}
            onClick={menu.id === "actions" ? (e) => handleClickMenu(e) : menu.action}
          >
            {menu.icon}
          </NavItem>
        ))}
      </Box>
    </NavBarStyle>
    <Snackbar
      open={refreshSnack.open}
      autoHideDuration={2500}
      onClose={() => setRefreshSnack({ open: false, message: "" })}
      message={refreshSnack.message}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    />
    </>
  );
};

export default NavBar;